import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Candle } from "@/components/Candle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { submitPayment } from "@/lib/payment";
import { titleCaseName } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Flame, ImagePlus, Loader2, X, Infinity as InfinityIcon } from "lucide-react";
import { PlanSelector } from "@/components/PlanSelector";
import { useCurrency } from "@/context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { NATIONALITIES, countryLabel } from "@/lib/nationalities";
import { PhotoCropDialog } from "@/components/PhotoCropDialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AuthDialog } from "@/components/AuthDialog";
import { Link } from "react-router-dom";

// Nationalité (adjectif) prioritaire selon la langue choisie sur le site.
const LANG_TO_NATIONALITY = {
  fr: "Française",
  en: "Britannique",
  es: "Espagnole",
  de: "Allemande",
  it: "Italienne",
  pl: "Polonaise",
  ru: "Russe",
  el: "Grecque",
  hi: "Indienne",
  ja: "Japonaise",
  zh: "Chinoise",
};

export default function CreateCandle({ publicMode = false }) {
  const { user, loading, setUser } = useAuth();
  const { format, currency } = useCurrency();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fileRef = useRef();
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [launchMeta, setLaunchMeta] = useState(null);
  const [nationality, setNationality] = useState("");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("decouverte");
  const [submitting, setSubmitting] = useState(false);
  const [candleSize, setCandleSize] = useState(300);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPay, setPendingPay] = useState(false);
  const DRAFT_KEY = "bdc_candle_draft";
  const DRAFT_ID_KEY = "bdc_candle_draft_id";

  const orderedNationalities = useMemo(() => {
    const lang = (i18n.language || "fr").split("-")[0];
    const priorityValue = LANG_TO_NATIONALITY[lang] || "Française";
    const loc = (i18n.language || "fr").replace("_", "-");
    const all = NATIONALITIES.map((n) => ({ ...n, label: countryLabel(n, i18n.language) }));
    const priority = all.find((n) => n.value === priorityValue);
    const rest = all.filter((n) => n.value !== priorityValue)
      .sort((a, b) => a.label.localeCompare(b.label, loc));
    return priority ? [priority, ...rest] : rest;
  }, [i18n.language]);

  useEffect(() => {
    const upd = () => setCandleSize(window.innerWidth >= 768 ? 440 : 300);
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  // Restaure le brouillon si un paiement précédent a échoué / été annulé.
  useEffect(() => {
    // Parcours public (Toussaint) : formulaire TOUJOURS vide, à remplir par le client.
    if (publicMode) {
      try { localStorage.removeItem(DRAFT_KEY); localStorage.removeItem(DRAFT_ID_KEY); } catch { /* noop */ }
      return;
    }
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { saved = null; }
    if (saved && (saved.name || saved.message || saved.photo)) {
      if (saved.name) setName(saved.name);
      if (saved.message) setMessage(saved.message);
      if (saved.nationality) setNationality(saved.nationality);
      if (saved.photo) setPhoto(saved.photo);
    }
    const draftId = localStorage.getItem(DRAFT_ID_KEY);
    if (draftId) {
      api.get(`/candles/${draftId}`).then((r) => {
        const c = r.data || {};
        if (c.paid || c.status === "active") {
          // La bougie a bien été payée entre-temps : on nettoie le brouillon.
          localStorage.removeItem(DRAFT_KEY);
          localStorage.removeItem(DRAFT_ID_KEY);
          setName(""); setMessage(""); setNationality(""); setPhoto(null);
        } else if (saved && (saved.name || saved.photo)) {
          toast.info(t("create.draftRestored", { defaultValue: "Nous avons conservé les informations de votre bougie non finalisée." }));
        }
      }).catch(() => { localStorage.removeItem(DRAFT_ID_KEY); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!publicMode && !loading && !user) navigate("/");
  }, [loading, user, navigate, publicMode]);

  // Parcours public (Toussaint) : après inscription/connexion, reprend automatiquement le paiement.
  useEffect(() => {
    if (publicMode && pendingPay && user) {
      setPendingPay(false);
      doCreateAndPay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingPay, publicMode]);

  useEffect(() => {
    if (publicMode) {
      // Offre Toussaint : 1 €, bougie allumée jusqu'au 26/12/2026 (fixé côté serveur).
      setPlans([{ code: "toussaint", label: t("tss.offerTitle"), hours: 1, price_cents: 100, i18n: false }]);
      setSelectedPlan("toussaint");
      setLaunchMeta(null);
      return;
    }
    api.get("/settings").then((r) => {
      const base = r.data.plans || [];
      const lo = r.data.launch_offer;
      if (lo && lo.active) {
        const launchPlan = { code: "lancement", label: lo.label || t("create.launchPlan"), hours: lo.hours || 720, price_cents: lo.price_cents ?? 100, i18n: false };
        setPlans(lo.exclusive ? [launchPlan] : [launchPlan, ...base]);
        setSelectedPlan("lancement");
        setLaunchMeta({ candle_until: lo.candle_until || null, ends_at: lo.ends_at || null });
      } else {
        setPlans(base);
        setLaunchMeta(null);
      }
    }).catch(() => {});
  }, [t, publicMode]);

  const currentPlan = plans.find((p) => p.code === selectedPlan);
  const price = currentPlan?.price_cents ?? 100;
  const fmtLong = (iso) => { try { return new Date(iso).toLocaleDateString((i18n.language || "fr").replace("_", "-"), { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; } };
  const eternalFree = (user?.free_eternal_candles || 0) > 0;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 40 * 1024 * 1024) return toast.error(t("create.photoTooBig"));
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const publicComplete = !!(photo && name.trim() && message.trim() && nationality);

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) return toast.error(t("create.photoRequired"));
    if (publicMode && !publicComplete) return toast.error(t("tss.fillAll"));
    // Sauvegarde locale des infos pour ne rien perdre (inscription ou échec paiement).
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ name, message, nationality, photo })); } catch { /* quota */ }
    // Parcours public Toussaint : demander l'inscription juste avant de payer.
    if (publicMode && !user) {
      setPendingPay(true);
      setAuthOpen(true);
      return;
    }
    doCreateAndPay();
  };

  const doCreateAndPay = async () => {
    setSubmitting(true);
    try {
      const payload = {
        deceased_name: name,
        tribute_message: message,
        photo_base64: photo,
        birth_date: null,
        death_date: null,
        nationality: nationality || null,
      };
      // Réutilise la bougie brouillon existante (paiement précédent annulé) au lieu d'en recréer une.
      let candleId = localStorage.getItem(DRAFT_ID_KEY);
      if (candleId) {
        try { await api.patch(`/candles/${candleId}`, payload); }
        catch { candleId = null; }
      }
      if (!candleId) {
        const created = await api.post("/candles", payload);
        candleId = created.data.id;
      }
      localStorage.setItem(DRAFT_ID_KEY, candleId);
      const clearDraft = () => { localStorage.removeItem(DRAFT_KEY); localStorage.removeItem(DRAFT_ID_KEY); };
      if (eternalFree) {
        const res = await api.post(`/me/candles/${candleId}/use-free-eternal`);
        if (user) setUser({ ...user, free_eternal_candles: res.data.free_eternal_candles });
        clearDraft();
        toast.success(t("ambassador.litToast"));
        navigate("/mes-bougies");
        return;
      }
      const pay = await api.post(`/candles/${candleId}/pay`, { origin_url: window.location.origin, plan: selectedPlan, currency });
      if (submitPayment(pay.data)) {
        return;
      } else {
        clearDraft();
        toast.success(t("create.candleLit"));
        navigate(`/candle/${candleId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("create.error"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-black">
      {publicMode ? (
        <header className="sticky top-0 z-30 flex items-center justify-center border-b border-white/5 bg-black/90 px-5 py-2 backdrop-blur" style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}>
          <Link to="/toussaint-2026" data-testid="toussaint-funnel-logo">
            <img src="/toussaint-logo.png" alt={t("tss.logoAlt")} className="h-14 w-auto sm:h-16" />
          </Link>
          <div className="absolute right-4 top-1/2 -translate-y-1/2" data-testid="toussaint-funnel-lang"><LanguageSwitcher /></div>
        </header>
      ) : (
        <Header />
      )}
      <div className={`mx-auto grid max-w-5xl px-5 md:grid-cols-2 ${publicMode ? "items-center gap-4 py-3 pb-6" : "gap-8 py-8 pb-28"}`}>
        <div className={`flex-col items-center justify-center rounded-3xl bg-black ${publicMode ? "hidden p-3 md:flex" : "flex p-6"}`}>
          <Candle progress={1} extinguished size={publicMode ? Math.round(candleSize * 0.6) : candleSize} />
          <p className={`text-center font-light text-amber-200/90 ${publicMode ? "mt-2 text-base" : "mt-4 text-lg"}`} data-testid="create-tagline">{t("create.tagline")}</p>
        </div>

        <form onSubmit={submit} className={`fade-in ${publicMode ? "space-y-3" : "space-y-5"}`} data-testid="create-candle-form">
          <div>
            <h1 className="text-2xl font-medium">{t("create.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("create.subtitle")}</p>
          </div>

          <div className="flex flex-col items-center">
            {photo ? (
              <div className="relative w-fit">
                <img src={photo} alt={name || t("create.addPhoto")} className={`rounded-full border-2 border-amber-300/80 object-cover shadow-[0_0_24px_rgba(251,191,36,0.35)] ${publicMode ? "h-20 w-20" : "h-28 w-28"}`} />
                <button type="button" onClick={() => setPhoto(null)} className="absolute -right-2 -top-2 rounded-full bg-destructive p-1" data-testid="remove-photo-btn">
                  <X size={14} />
                </button>
                <button type="button" onClick={() => setCropSrc(photo)} className="mt-2 block w-full text-center text-xs text-sky-400 underline hover:text-sky-300" data-testid="reposition-photo-btn">
                  {t("cropper.edit")}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} className={`flex flex-col items-center justify-center rounded-full border-2 border-dashed border-amber-300/40 px-2 text-center text-muted-foreground transition-colors hover:border-amber-300 hover:text-amber-300 ${publicMode ? "h-20 w-20" : "h-28 w-28"}`} data-testid="add-photo-btn">
                <ImagePlus size={22} />
                <span className="mt-1 text-xs leading-tight">{t("create.addPhoto")}</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
            <PhotoCropDialog open={!!cropSrc} image={cropSrc} onCancel={() => setCropSrc(null)} onCropped={(b64) => { setPhoto(b64); setCropSrc(null); }} />

            {/* Dates ajoutées après paiement */}
            {!publicMode && (
              <p className="mt-4 w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-center text-xs text-muted-foreground" data-testid="create-dates-later-note">
                {t("create.datesLater")}
              </p>
            )}
            <div className="mt-3">
              <Label htmlFor="nationality" className="text-xs text-muted-foreground">{publicMode ? t("tss.natLabel") : t("create.nationalityLabel")}</Label>
              <select id="nationality" value={nationality} onChange={(e) => setNationality(e.target.value)} data-testid="candle-nationality" className="mt-1 h-10 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white outline-none focus:border-amber-300">
                <option value="">{publicMode ? t("tss.natChoose") : t("create.nationalityNone")}</option>
                {orderedNationalities.map((n) => (
                  <option key={n.value} value={n.value} className="bg-neutral-900">{n.flag} {n.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="name">{t("create.nameLabel")}</Label>
            <Input id="name" required value={name} onChange={(e) => setName(titleCaseName(e.target.value))} placeholder={t("create.namePlaceholder")} data-testid="candle-name-input" />
          </div>

          <div>
            <Label htmlFor="msg">{t("create.messageLabel")}</Label>
            <Textarea id="msg" rows={publicMode ? 2 : 4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("create.messagePlaceholder")} data-testid="candle-message-input" />
          </div>

          {eternalFree ? (
            <div className="rounded-2xl border border-amber-400/25 bg-black px-4 py-3 text-sm text-amber-200" data-testid="eternal-free-notice">
              <InfinityIcon size={16} className="mr-1.5 inline" /> {t("ambassador.createNotice")}
            </div>
          ) : (
            <div>
              <Label>{t("create.durationLabel")}</Label>
              <p className="mt-0.5 text-sm text-sky-400" data-testid="create-offer-sub">{t("home.ctaFree")}</p>
              <div className="mt-2">
                <PlanSelector plans={plans.length === 1 ? plans.map((p) => ({ ...p, label: t("tss.offerTitle"), i18n: false })) : plans} value={selectedPlan} onChange={setSelectedPlan} />
              </div>
              {publicMode && (
                <p className="mt-3 rounded-2xl border border-sky-400/30 bg-sky-400/[0.07] px-4 py-3 text-center text-base font-semibold text-sky-100" data-testid="tss-until-deadline">{t("tss.untilDeadline")}</p>
              )}
              {launchMeta && (launchMeta.candle_until || launchMeta.ends_at) && (
                <div className="mt-3 rounded-2xl border border-sky-400/30 bg-sky-400/[0.07] px-4 py-3 text-center" data-testid="launch-offer-dates">
                  {launchMeta.ends_at ? (
                    <p className="text-lg font-bold text-sky-100" data-testid="offer-deadline">
                      {t("create.offerDeadline", { date: fmtLong(launchMeta.ends_at), defaultValue: "Offre à valider avant le {{date}}" })}
                    </p>
                  ) : (launchMeta.candle_until && (
                    <p className="text-base font-semibold text-sky-100">
                      {t("create.candleFreeUntil", { date: fmtLong(launchMeta.candle_until), defaultValue: "Bougie allumée gratuitement jusqu'au {{date}}" })}
                    </p>
                  ))}
                  {launchMeta.ends_at && launchMeta.candle_until && (
                    <p className="mt-2 text-sm leading-relaxed text-sky-200/90">
                      {t("create.offerIncentive", { end: fmtLong(launchMeta.ends_at), until: fmtLong(launchMeta.candle_until), defaultValue: "Si vous validez cette offre avant le {{end}}, votre bougie restera allumée gratuitement jusqu'au {{until}}." })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <Button type="submit" disabled={submitting || !photo || (publicMode && !publicComplete)} className="h-13 w-full rounded-full py-6 text-base font-semibold shadow-[0_4px_24px_0_rgba(56,189,248,0.4)]" data-testid="pay-and-light-btn">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : publicMode ? <><Flame size={18} className="mr-2" /> {t("tss.lightMyCandle")}</> : eternalFree ? <><InfinityIcon size={18} className="mr-2" /> {t("ambassador.createButton")}</> : <><Flame size={18} className="mr-2" /> {t("create.payButton", { price: format(price) })}</>}
          </Button>
          {publicMode && !publicComplete && (
            <p className="text-center text-xs text-muted-foreground" data-testid="tss-fill-all-hint">{t("tss.fillAll")}</p>
          )}
        </form>
      </div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode="register" onSuccess={() => setAuthOpen(false)} />
    </div>
  );
}
